import { Component, Element, Event, EventEmitter, JSX, Method, Prop, Watch, h } from '@stencil/core'

import { KeyCode } from '../../enums/keycode.enum'
import { RadioEventDetail } from './air-radio-button.types'
import { Validation } from '../../types/status.types'

@Component({
  tag: 'air-radio-button',
  styleUrl: 'air-radio-button.scss',
  shadow: true,
})
export class AirRadioButton {
  /** Reference to the host element */
  @Element() host!: HTMLAirRadioButtonElement

  /** Defines name */
  @Prop({ reflect: true }) name!: string

  /** Defines label */
  @Prop() label!: string

  /** Defines value */
  @Prop() value!: string | boolean

  /** Defines disabled */
  @Prop() disabled = false

  /** Defines if radio button is required */
  @Prop() required = false

  /** Specifies validation of radio button */
  @Prop() validation?: Validation

  /** Defines if radio button is checked */
  @Prop({ reflect: true }) checked = false
  @Watch('checked')
  watchCheckedChange(): void {
    if (this.checked) {
      this.radioButtonElement && this.radioButtonElement.focus && this.radioButtonElement.focus()
    }
  }

  /** Defines if radio button should have autofocus */
  // eslint-disable-next-line @stencil-community/reserved-member-names
  @Prop() autofocus = false

  /** Defines if radio button should be focusable */
  @Prop() focusable = false

  private radioButtonElement: HTMLDivElement | undefined
  private radioButtonEventDetail: RadioEventDetail | undefined

  /** Emits value on change */
  @Event({ eventName: 'air-change' }) change!: EventEmitter<RadioEventDetail>

  /** Emits value on focus */
  @Event({ eventName: 'air-focus' }) focus!: EventEmitter<RadioEventDetail>

  /** Emits value on blur */
  @Event({ eventName: 'air-blur' }) blur!: EventEmitter<RadioEventDetail>

  /** To focus radio button */
  @Method()
  async setFocus(): Promise<void> {
    this.radioButtonElement && this.radioButtonElement.focus && this.radioButtonElement.focus()
  }

  async componentDidLoad(): Promise<void> {
    if (this.autofocus) {
      await this.setFocus()
    }

    await this.setFocusable()
  }

  private get radioButtons(): HTMLAirRadioButtonElement[] {
    const root = this.host.getRootNode() as HTMLElement
    return Array.from(
      root.querySelectorAll<HTMLAirRadioButtonElement>(`air-radio-button[name=${this.host.name}]`),
    )
  }

  private setFocusable = async (): Promise<void> => {
    await Promise.resolve()
    const findCheckedCheckbox = this.radioButtons.filter((radio: HTMLAirRadioButtonElement) => {
      return radio.getAttribute('checked') !== null
    })

    if (
      findCheckedCheckbox?.length === 0 &&
      this.radioButtons?.length > 0 &&
      !this.radioButtons[0].disabled
    ) {
      this.radioButtons[0].focusable = true
      return
    }

    this.radioButtons.forEach((radio: HTMLAirRadioButtonElement) => {
      const focusable = findCheckedCheckbox[0] === radio
      radio.setAttribute('focusable', focusable.toString())
    })
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    event.stopPropagation()
    const actions: Record<string, (event: KeyboardEvent) => void> = {
      [KeyCode.Space]: this.handleClick,
      [KeyCode.Enter]: this.handleClick,
      [KeyCode.Up]: this.setCheckedToPreviousItem,
      [KeyCode.Right]: this.setCheckedToNextItem,
      [KeyCode.Down]: this.setCheckedToNextItem,
      [KeyCode.Left]: this.setCheckedToPreviousItem,
    }
    actions[event.code] && actions[event.code](event)
  }

  private setCheckedToPreviousItem = (event: KeyboardEvent): void => {
    event.preventDefault()
    let previousIndex = this.radioButtons.indexOf(this.host) - 1
    if (previousIndex < 0) {
      previousIndex = this.radioButtons.length - 1
    }
    this.setChecked(previousIndex)
    this.emitEvent(this.change)
  }

  private setCheckedToNextItem = (event: KeyboardEvent): void => {
    event.preventDefault()
    let nextIndex = this.radioButtons.indexOf(this.host) + 1

    if (nextIndex >= this.radioButtons.length) {
      nextIndex = 0
    }

    this.setChecked(nextIndex)
    this.emitEvent(this.change)
  }

  private handleFocus = (event: FocusEvent): void => {
    event.stopPropagation()
    if (this.disabled) return
    this.emitEvent(this.focus)
  }

  private handleBlur = (event: FocusEvent): void => {
    event.stopPropagation()
    if (this.disabled) return
    this.emitEvent(this.blur)
  }

  private handleClick = (): void => {
    if (this.disabled) return

    const clickedIndex = this.radioButtons.indexOf(this.host)
    this.setChecked(clickedIndex)
    this.emitEvent(this.change)
  }

  private setChecked(index: number): void {
    this.radioButtons.forEach((el, i) => {
      if (index === i) {
        el.checked = true
        el.focusable = true
      } else {
        el.checked = false
        el.focusable = false
      }
    })
  }

  private emitEvent(emitter: EventEmitter): void {
    const checkedRadio = this.radioButtons.find(
      (el: HTMLAirRadioButtonElement) => el.checked === true,
    )
    if (checkedRadio === undefined) return
    this.radioButtonEventDetail = {
      name: checkedRadio.name,
      value: checkedRadio.value,
    }
    emitter?.emit(this.radioButtonEventDetail)
  }

  private renderLabel(): HTMLSpanElement | null {
    return this.label !== undefined ? (
      <span class="label">
        {this.label}
        {this.required && <span class="required-mark">*</span>}
      </span>
    ) : null
  }

  render(): JSX.Element {
    const statusType = this.validation?.status
    const radioButtonClasses = {
      'radio-button': true,
      disabled: this.disabled,
      [`status-${statusType}`]: statusType !== undefined,
    }
    const buttonAttributes = {
      class: radioButtonClasses,
      role: 'radio',
      'aria-checked': this.checked,
      tabindex: this.focusable ? '0' : '-1',
      onClick: this.handleClick,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onKeydown: this.handleKeyDown,
      ref: (el: HTMLDivElement | undefined): void => {
        this.radioButtonElement = el
      },
    }
    return <div {...buttonAttributes}>{this.renderLabel()}</div>
  }
}
